import { useMemo } from "react";
import { SectionHead } from "./shared";

// =========================================================================
// TERMS & CONDITIONS  (own tab, always expanded)
// =========================================================================
export function TermsTab({ d, narrativeLoading }) {
  const paragraphs = d.narrative_terms || [];
  const tables = d.terms_tables || [];
  // Same clause-detection used previously in TermsSection, plus
  // [[TABLE:n]] markers (canonical fee tables) and "• " bullets.
  const items = useMemo(() => {
    const out = [];
    const clauseRe = /^(\d+(?:\.\d+)*)\s+([A-Z][^:]{2,80}):\s*(.*)$/;
    const tableRe = /^\[\[TABLE:(\d+)\]\]$/;
    for (const p of paragraphs) {
      const tm = p.match(tableRe);
      if (tm) {
        out.push({ kind: "table", index: Number(tm[1]) });
        continue;
      }
      if (p.startsWith("• ")) {
        out.push({ kind: "bullet", text: p.slice(2) });
        continue;
      }
      const m = p.match(clauseRe);
      if (m) {
        out.push({ kind: "clause", number: m[1], title: m[2].trim(), body: m[3].trim() });
      } else if (/^\d+\.\s+[A-Z]/.test(p) && p.length < 100) {
        out.push({ kind: "heading", text: p });
      } else {
        out.push({ kind: "para", text: p });
      }
    }
    return out;
  }, [paragraphs]);

  return (
    <div className="space-y-8" data-testid="terms-tab">
      <SectionHead n="05" title="Terms & Conditions of Business" />
      {paragraphs.length > 0 ? (
        <div className="bg-white border border-stone-200 rounded-xl p-6 lg:p-8 text-stone-700 text-[14px] leading-relaxed">
          {items.map((it, i) => {
            if (it.kind === "heading") {
              return (
                <h3
                  key={i}
                  className="text-base font-semibold text-stone-900 mt-6 mb-2 first:mt-0"
                >
                  {it.text}
                </h3>
              );
            }
            if (it.kind === "clause") {
              return (
                <p key={i} className="mb-3">
                  <span className="font-mono text-xs text-stone-500 mr-2">
                    {it.number}
                  </span>
                  <span className="font-medium text-stone-900">
                    {it.title}:
                  </span>{" "}
                  <span>{it.body}</span>
                </p>
              );
            }
            if (it.kind === "table") {
              return <FeeTable key={i} table={tables[it.index]} index={it.index} />;
            }
            if (it.kind === "bullet") {
              const ci = it.text.indexOf(":");
              const lead = ci > 0 ? it.text.slice(0, ci) : null;
              const rest = ci > 0 ? it.text.slice(ci + 1).trim() : it.text;
              return (
                <p key={i} className="mb-3 flex gap-2">
                  <span className="text-[#3EA8DC] shrink-0">•</span>
                  <span>
                    {lead && (
                      <span className="font-medium text-stone-900">{lead}: </span>
                    )}
                    {rest}
                  </span>
                </p>
              );
            }
            return (
              <p key={i} className="mb-3 last:mb-0">{it.text}</p>
            );
          })}
        </div>
      ) : (
        <p
          className="text-sm text-stone-400 animate-pulse"
          data-testid="narrative-loading-terms"
        >
          {narrativeLoading ? "Loading terms…" : "No terms & conditions available."}
        </p>
      )}
    </div>
  );
}

// Cancellation fee schedules from the canonical T&Cs PDF (section 13).
function FeeTable({ table, index }) {
  if (!table) return null;
  return (
    <div className="my-5" data-testid={`terms-fee-table-${index}`}>
      <p className="font-medium text-stone-900 mb-0.5">{table.title}</p>
      {table.caption && (
        <p className="text-xs text-stone-500 mb-2">{table.caption}</p>
      )}
      <div className="border border-stone-200 rounded-lg overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-stone-100/60 text-left">
              {table.columns.map((c, i) => (
                <th
                  key={i}
                  className={`px-3 py-2 font-medium text-stone-700 whitespace-nowrap ${
                    i > 0 ? "text-right" : ""
                  }`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r, ri) => (
              <tr key={ri} className="border-t border-stone-100">
                {r.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-3 py-2 ${
                      ci === 0
                        ? "text-stone-800"
                        : "font-mono text-right text-stone-900"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.footnote && (
        <p className="text-[11px] text-stone-500 mt-1.5">{table.footnote}</p>
      )}
    </div>
  );
}
